/**
 * Redux reducer for the Batch Tables (Tabular Review) feature.
 *
 * Registered in the store as "batchTables" to avoid collision
 * with the existing "tables" reducer (SSE table-generation).
 */

import { AnyAction } from 'redux';
import { BatchTablesState, TableListItem, TableRow, CellComment } from '../../../types/batchTables';
import {
  BATCH_TABLES_LOADING,
  BATCH_TABLES_FETCH_LIST,
  BATCH_TABLES_APPEND_LIST,
  BATCH_TABLES_SET_DETAIL,
  BATCH_TABLES_LOADING_DETAIL,
  BATCH_TABLES_UPDATE_IN_LIST,
  BATCH_TABLES_REMOVE_FROM_LIST,
  BATCH_TABLES_SET_TEMPLATES,
  BATCH_TABLES_LOADING_TEMPLATES,
  BATCH_TABLES_SET_UPLOADING,
  BATCH_TABLES_SET_PROCESSING,
  BATCH_TABLES_ERROR,
  BATCH_TABLES_UPDATE_CELL,
  BATCH_TABLES_UPDATE_ROW_STATUS,
  BATCH_TABLES_UPDATE_TABLE_STATUS,
  BATCH_TABLES_RESET_COLUMN_CELLS,
  BATCH_TABLE_CREATE_RESET,
  BATCH_TABLE_CREATE_CONNECTING,
  BATCH_TABLE_CREATE_CONNECTED,
  BATCH_TABLE_CREATE_STEPS,
  BATCH_TABLE_CREATE_STEP,
  BATCH_TABLE_CREATE_COMPLETED,
  BATCH_TABLE_CREATE_FAILED,
  BATCH_TABLES_SET_CELL_COMMENTS,
  BATCH_TABLES_LOADING_CELL_COMMENTS,
  BATCH_TABLES_ADD_CELL_COMMENT,
  BATCH_TABLES_UPDATE_CELL_COMMENT,
  BATCH_TABLES_REMOVE_CELL_COMMENT,
} from '../../actions/BatchTables/BatchTablesActions';

type UiStatus = 'Pending' | 'In Progress' | 'Completed' | 'Failed';
const FROM_RAW: Record<string, UiStatus> = {
  pending: 'Pending',
  in_progress: 'In Progress',
  completed: 'Completed',
  failed: 'Failed',
};

const sseInitial = {
  isConnecting: false,
  connected: false,
  steps: [] as { id: string; label: string; status: UiStatus; messages: string[] }[],
  complete: false,
  ok: null as boolean | null,
  summary: null as string | null,
};

const initialState: BatchTablesState = {
  items: [],
  totalItems: 0,
  currentPage: 1,
  totalPages: 1,
  isLoading: false,

  currentTable: null,
  isLoadingDetail: false,

  templates: [],
  isLoadingTemplates: false,

  isProcessing: false,
  isUploading: false,

  cellComments: [],
  isLoadingComments: false,

  sseCreate: { ...sseInitial },

  error: null,
};

const batchTablesReducer = (state = initialState, action: AnyAction): BatchTablesState => {
  switch (action.type) {
    // ─── List loading ───────────────────────────────
    case BATCH_TABLES_LOADING:
      return { ...state, isLoading: action.payload, error: null };

    case BATCH_TABLES_FETCH_LIST:
      return {
        ...state,
        items: action.payload.items,
        totalItems: action.payload.total,
        currentPage: action.payload.page,
        totalPages: action.payload.total_pages,
      };

    case BATCH_TABLES_APPEND_LIST:
      return {
        ...state,
        items: [...state.items, ...action.payload.items],
        totalItems: action.payload.total,
        currentPage: action.payload.page,
        totalPages: action.payload.total_pages,
      };

    // ─── Detail ─────────────────────────────────────
    case BATCH_TABLES_SET_DETAIL: {
      const incoming = action.payload;

      // Guard: if we optimistically set the table to processing/queued and
      // a stale fetch arrives with the old completed/pending status, protect
      // the optimistic state so skeleton loaders aren't wiped out.
      if (
        incoming &&
        state.currentTable &&
        state.currentTable.uuid === incoming.uuid &&
        ['processing', 'queued'].includes(state.currentTable.status) &&
        !['processing', 'queued'].includes(incoming.status)
      ) {
        // Merge: take structural changes (columns, rows list, metadata)
        // from the server but preserve our optimistic status + cell states.
        return {
          ...state,
          currentTable: {
            ...incoming,
            status: state.currentTable.status,
            rows: state.currentTable.rows,
          },
        };
      }

      return { ...state, currentTable: incoming };
    }

    case BATCH_TABLES_LOADING_DETAIL:
      return { ...state, isLoadingDetail: action.payload, error: null };

    // ─── Update item in list ────────────────────────
    case BATCH_TABLES_UPDATE_IN_LIST: {
      const updated = action.payload;
      return {
        ...state,
        items: state.items.map((item: TableListItem) =>
          item.uuid === updated.uuid ? { ...item, ...updated } : item
        ),
      };
    }

    // ─── Remove from list ───────────────────────────
    case BATCH_TABLES_REMOVE_FROM_LIST:
      return {
        ...state,
        items: state.items.filter((item: TableListItem) => item.uuid !== action.payload),
        totalItems: Math.max(0, state.totalItems - 1),
      };

    // ─── Templates ──────────────────────────────────
    case BATCH_TABLES_SET_TEMPLATES:
      return { ...state, templates: action.payload };

    case BATCH_TABLES_LOADING_TEMPLATES:
      return { ...state, isLoadingTemplates: action.payload };

    // ─── Upload / Processing flags ──────────────────
    case BATCH_TABLES_SET_UPLOADING:
      return { ...state, isUploading: action.payload };

    case BATCH_TABLES_SET_PROCESSING:
      return { ...state, isProcessing: action.payload };

    // ─── Error ──────────────────────────────────────
    case BATCH_TABLES_ERROR:
      return { ...state, error: action.payload };

    // ─── Real-time updates (WebPubSub) ──────────────
    case BATCH_TABLES_UPDATE_CELL: {
      if (!state.currentTable) return state;
      const { row_id, column_id, cell, cell_id, partial } = action.payload;

      // Partial update path: update specific fields on a cell found by cell_id
      if (cell_id && partial) {
        return {
          ...state,
          currentTable: {
            ...state.currentTable,
            rows: state.currentTable.rows.map((row: TableRow) => {
              const updatedCells = { ...row.cells };
              let changed = false;
              for (const colKey of Object.keys(updatedCells)) {
                if (updatedCells[colKey]?.id === cell_id) {
                  updatedCells[colKey] = { ...updatedCells[colKey], ...partial };
                  changed = true;
                  break;
                }
              }
              return changed ? { ...row, cells: updatedCells } : row;
            }),
          },
        };
      }

      // Full cell replacement path (PubSub live updates)
      // Merge with existing cell data to preserve fields like 'id' that PubSub doesn't include
      return {
        ...state,
        currentTable: {
          ...state.currentTable,
          cells_completed: state.currentTable.cells_completed + (cell.status === 'completed' ? 1 : 0),
          rows: state.currentTable.rows.map((row: TableRow) =>
            row.id === row_id
              ? {
                  ...row,
                  status: row.status === 'pending' ? 'processing' : row.status,
                  cells: { ...row.cells, [String(column_id)]: { ...row.cells[String(column_id)], ...cell } },
                }
              : row
          ),
        },
      };
    }

    case BATCH_TABLES_UPDATE_ROW_STATUS: {
      if (!state.currentTable) return state;
      const { row_id, status, processing_error } = action.payload;
      return {
        ...state,
        currentTable: {
          ...state.currentTable,
          rows: state.currentTable.rows.map((row: TableRow) =>
            row.id === row_id ? { ...row, status, processing_error } : row
          ),
        },
      };
    }

    case BATCH_TABLES_RESET_COLUMN_CELLS: {
      if (!state.currentTable) return state;
      const { columnId } = action.payload;
      const colKey = String(columnId);
      return {
        ...state,
        currentTable: {
          ...state.currentTable,
          rows: state.currentTable.rows.map((row: TableRow) => ({
            ...row,
            cells: {
              ...row.cells,
              [colKey]: row.cells[colKey]
                ? { ...row.cells[colKey], status: 'pending', value: null, source_excerpt: null, confidence: null }
                : row.cells[colKey],
            },
          })),
        },
      };
    }

    case BATCH_TABLES_UPDATE_TABLE_STATUS: {
      const { uuid, status, cells_completed, cells_total } = action.payload;
      const ct = state.currentTable;
      const updatedTable = ct && ct.uuid === uuid
        ? { ...ct, status, cells_completed: cells_completed ?? ct.cells_completed, cells_total: cells_total ?? ct.cells_total }
        : state.currentTable;

      return {
        ...state,
        currentTable: updatedTable,
        items: state.items.map((item: TableListItem) =>
          item.uuid === uuid ? { ...item, status, cells_completed: cells_completed ?? item.cells_completed, cells_total: cells_total ?? item.cells_total } : item
        ),
      };
    }

    // ─── SSE Creation Progress ──────────────────────
    case BATCH_TABLE_CREATE_RESET:
      return { ...state, sseCreate: { ...sseInitial } };

    case BATCH_TABLE_CREATE_CONNECTING:
      return { ...state, sseCreate: { ...state.sseCreate, isConnecting: true, connected: false } };

    case BATCH_TABLE_CREATE_CONNECTED:
      return { ...state, sseCreate: { ...state.sseCreate, isConnecting: false, connected: true } };

    case BATCH_TABLE_CREATE_STEPS:
      return {
        ...state,
        sseCreate: {
          ...state.sseCreate,
          steps: (action.payload?.steps || []).map((s: any) => ({
            id: String(s.id),
            label: s.name || s.label || `Step ${s.id}`,
            status: FROM_RAW[s.status] || 'Pending',
            messages: [],
          })),
        },
      };

    case BATCH_TABLE_CREATE_STEP: {
      const stepData = action.payload?.step || action.payload || {};
      const { id, status, message, name } = stepData;
      const stepId = String(id);
      const ui = FROM_RAW[status] || 'Pending';
      const steps = [...state.sseCreate.steps];
      const i = steps.findIndex(s => s.id === stepId);
      if (i === -1) {
        steps.push({ id: stepId, label: name || stepId, status: ui, messages: message ? [message] : [] });
      } else {
        steps[i] = {
          ...steps[i],
          status: ui,
          messages: message ? [...steps[i].messages, message] : steps[i].messages,
        };
      }
      return { ...state, sseCreate: { ...state.sseCreate, steps } };
    }

    case BATCH_TABLE_CREATE_COMPLETED:
      return {
        ...state,
        sseCreate: {
          ...state.sseCreate,
          complete: true,
          ok: action.payload?.ok ?? true,
          summary: action.payload?.message || 'Table created successfully',
        },
      };

    case BATCH_TABLE_CREATE_FAILED:
      return {
        ...state,
        sseCreate: {
          ...state.sseCreate,
          complete: true,
          ok: false,
          summary: action.payload?.error || 'Failed',
        },
      };

    // ─── Cell Comments ──────────────────────────────
    case BATCH_TABLES_SET_CELL_COMMENTS:
      return { ...state, cellComments: action.payload };

    case BATCH_TABLES_LOADING_CELL_COMMENTS:
      return { ...state, isLoadingComments: action.payload };

    case BATCH_TABLES_ADD_CELL_COMMENT:
      return { ...state, cellComments: [...state.cellComments, action.payload] };

    case BATCH_TABLES_UPDATE_CELL_COMMENT: {
      const updated = action.payload;
      return {
        ...state,
        cellComments: state.cellComments.map((c: CellComment) =>
          c.id === updated.id ? { ...c, ...updated } : c
        ),
      };
    }

    case BATCH_TABLES_REMOVE_CELL_COMMENT:
      return {
        ...state,
        cellComments: state.cellComments.filter((c: CellComment) => c.id !== action.payload.commentId),
      };

    default:
      return state;
  }
};

export default batchTablesReducer;
